/**
 * Fire-and-forget route-change beacon for the Sessions tab's Activity view ("which
 * screens/sites are most active"). Called once per actual navigation from
 * AppShell.tsx's Console() -- a useLocation()/useEffect keyed on location.pathname, not
 * a poll loop (this repo's CLAUDE.md "Live data architecture" rule).
 *
 * Site/screen derived from the exact route table AppShell.tsx's Console() renders
 * (`<Route>` list) -- kept here rather than imported, since the two sides of this
 * contract (what the client sends, what the server's own allowlist accepts) are
 * independently allowed to drift without breaking: a route this table doesn't recognize
 * falls back to 'unknown'/'unknown', never dropped or thrown on.
 */

//: First path segment -> site. 'mbmt' here covers BOTH /mbmt/reports/:mode? and
//: /mbmt/charging/:tab? (AppShell.tsx's own MbmtReportsShell route and its charging
//: redirect) -- same site as /bus-ops, a different first segment (adversarial review,
//: MED: this table used to have no 'mbmt' entry at all, so both of those real routes
//: silently reported as unknown/unknown, misattributing the whole MBMT reports shell).
const SITE_BY_FIRST_SEGMENT: Record<string, string> = {
  fleet: 'ultratech',
  reports: 'ultratech',
  'bus-ops': 'mbmt',
  'bus-ops-nagpur': 'nagpur',
  mbmt: 'mbmt',
  eim: 'eim',
  star: 'star',
  wondercement: 'wondercement',
  global: 'global',
  settings: 'settings',
}

export function trackScreenView(pathname: string): void {
  const segments = pathname.split('/').filter(Boolean)
  const first = segments[0] ?? ''
  // Bare "/" and anything under "/app" are never a real screen -- they are the two
  // landing pathnames every cold load/sign-in passes through on the way to a real
  // route, immediately replaced by AppShell.tsx's own redirect (its "/" route, and the
  // catch-all "*" route for "/app*" -- serving/app.py's `app.mount("/app",
  // StaticFiles(..., html=True))` answers index.html for ANY path under /app, and
  // main.tsx's BrowserRouter has no basename, so the live pathname really is "/app/"
  // -- or a stale "/app/..." deep link -- for one render before the redirect fires).
  // Counting these would write one junk unknown/unknown row per cold load into the
  // exact aggregate this ticket exists to produce (adversarial review round 2, MED).
  if (segments.length === 0 || first === 'app') return
  const site = SITE_BY_FIRST_SEGMENT[first] ?? 'unknown'
  // Every segment after the site, joined -- not just segments[1] -- so a nested route
  // like /mbmt/reports/ops reports screen "reports/ops", not just "reports" (losing
  // which mode) or "ops" (losing which shell). Single-segment routes (/bus-ops/live)
  // are unaffected: slice(1) of a 2-element array is still just that one element.
  const screen = site === 'unknown' ? 'unknown' : (segments.slice(1).join('/') || 'default')

  fetch('/api/telemetry/screen-view', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ site, screen, path: pathname }),
  }).catch(() => { /* telemetry, not a gate -- a dropped beacon costs nothing but a data point */ })
}
